import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useAppDispatch } from 'app/hooks';
import Icon from 'components/icons/Icon';
import { IconSymbol, IconVariant } from 'components/icons/Icon.types';
import Error404 from 'components/navigation/Error404';
import {
  SortableContainer,
  SortableInfo,
  SortableItem,
  SortableItemType,
  SortableList,
  SortableListInfo,
  SortableListType,
} from 'components/sortables/Sortable';
import { IPage } from 'features/page/pageAPI';
import { patchPageAsync } from 'features/page/pageSlice';
import { ISection } from 'features/page/sectionAPI';
import {
  patchSectionAsync,
  postSectionAsync,
} from 'features/page/sectionSlice';
import {
  getFareyOrderForLast,
  getFareyOrderUsingId,
} from 'helpers/farey-sorting';
import { AnyOrderObject, sameId } from 'helpers/objects';
import { sortByOrder } from 'helpers/sorting';
import { useSettingsReady } from 'hooks/useAppReadiness';
import { useLocation } from 'react-router-dom';
import { useSite } from 'selectors/siteSelectors';
import { useUser } from 'selectors/userSelectors';
import Section from './Section';

import { canEdit } from 'features/user/userSlice';
import { getLanguageName } from 'helpers/intl/intlHelpers';
import '../static/StaticPage.scss';
import LiveContentWarning from './LiveContentWarning';
import { PageContextProvider } from './PageContext';
import './Pages.scss';

type PageProps = {
  slug?: string;
  className?: string;
};

export default function Page(props: PageProps) {
  const { slug, className } = props;

  const { locale, user } = useUser();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const settingsReady = useSettingsReady();
  const { pages, fetchStatus } = useSite();

  const [content, setContent] = useState<string>('');
  const scrollableRef = useRef<HTMLDivElement>(null!);
  const [page, setPage] = useState<IPage | null>(null);
  const [editing, setEditing] = useState<boolean>(false);
  const [sorting, setSorting] = useState<boolean>(false);
  const [notfound, setNotfound] = useState<boolean>(false);
  const editable = useMemo(() => canEdit(user, page), [user, page]);

  const scrollToFragment = useCallback((hash: string) => {
    const targetId = hash.replace(/^#/, '');
    if (!targetId) return false;
    const scrollable = scrollableRef.current;
    const target = document.getElementById(targetId);
    if (!scrollable || !target) return false;
    const targetRect = target.getBoundingClientRect();
    const containerRect = scrollable.getBoundingClientRect();
    scrollable.scrollBy({
      top: targetRect.top - containerRect.top - 70,
      behavior: 'smooth',
    });
    return true;
  }, []);

  useEffect(() => {
    // Wait for settings to be initialized before using settings data
    if (!settingsReady || fetchStatus.pages === 'loading') return;
    async function getContent() {
      if (slug) {
        // const html = await getHtml(slug, locale);
        // setContent(html);
        const p = pages.find((p) => p.slug === slug && p.locale === locale);
        if (p) {
          setPage(p);
          setNotfound(false);
        } else {
          setPage(null);
          setNotfound(true);
        }
      }
    }
    getContent();
  }, [slug, locale, settingsReady, pages, fetchStatus.pages]);

  useEffect(() => {
    if (!location.hash) return;
    const frame = window.requestAnimationFrame(() => {
      scrollToFragment(location.hash);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [location.hash, page, scrollToFragment]);

  useEffect(() => {
    const scrollable = scrollableRef.current;
    if (!scrollable || editing) return;
    function handleClick(event: MouseEvent) {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }
      const target = event.target;
      if (!(target instanceof Element)) return;
      const anchor = target.closest('a[href^="#"]');
      if (!(anchor instanceof HTMLAnchorElement)) return;
      const href = anchor.getAttribute('href');
      if (!href) return;
      event.preventDefault();
      window.history.pushState(null, '', href);
      scrollToFragment(href);
    }
    scrollable.addEventListener('click', handleClick);
    return () => scrollable.removeEventListener('click', handleClick);
  }, [editing, scrollToFragment]);

  useEffect(() => {
    // add email address where required
    const m = document.getElementById('mContainer');
    if (m && m instanceof HTMLAnchorElement) {
      let email = 'ln.ygolonhcetcivic@ofni'.split('').reverse().join('');
      m.href = 'mailto:' + email;
      m.textContent += ' ' + email;
    }
    // add event listener for unmuting video
    if (scrollableRef.current && content && content !== '') {
      const videoElement = scrollableRef.current.querySelector('video');
      if (videoElement) {
        const toggleMute = (event: Event) => {
          const video = event.currentTarget as HTMLVideoElement;
          if (video.muted) {
            video.muted = false;
            video.volume = 1.0;
            video.title = 'Click to mute';
          } else {
            video.muted = true;
            video.title = 'Click to unmute';
          }
        };
        videoElement.addEventListener('click', toggleMute);
        return () => {
          videoElement.removeEventListener('click', toggleMute);
        };
      }
    }
  }, [content]);

  const reorderSections = useCallback(
    async (
      sortableInfo: SortableInfo,
      sourceItem: AnyOrderObject,
      sourceList: SortableListInfo,
      destinationItem?: AnyOrderObject,
      destinationList?: SortableListInfo,
      copy?: boolean,
    ) => {
      console.log('reordering sections');
      const data: ISection = {
        ...sourceItem,
      };
      // set the new order attribute
      const before = destinationItem
        ? ['top', 'left'].includes(
            extractClosestEdge(
              sortableInfo.location.current.dropTargets[0].data,
            ) ?? '',
          )
        : false;
      data.farey_order = getFareyOrderUsingId(
        sortByOrder(
          destinationList?.items?.filter(
            (item) => !item.pinned,
          ) as AnyOrderObject[],
        ),
        sameId(sourceList, destinationList) ? sourceItem.id : -1, // -1 if moving between lists
        destinationItem ? destinationItem.id : -1,
        before,
      );
      dispatch(patchSectionAsync({ data: data })).then(
        (response: { payload: any }) => {
          switch (response.payload?.response.status) {
            case 200:
            case 201:
              // console.log('Blocks updated');
              break;
            case 500:
            default:
              // TODO: need a better action here, possibly force reload.
              console.log(
                'Page not updated due to internal error. Please reload the page.',
              );
              break;
          }
        },
      );
    },
    [dispatch],
  );

  const updateTitle = useCallback(
    (e: React.FocusEvent<HTMLHeadingElement>) => {
      console.log('page:', page);
      const target = e.currentTarget;
      dispatch(
        patchPageAsync({
          data: {
            ...page,
            title: target.textContent ?? '',
          } as IPage,
        }),
      );
    },
    [dispatch, page],
  );

  function handleAddSection(): void {
    if (!page) return;
    const sortedSections = sortByOrder(
      (page.sections ?? []) as AnyOrderObject[],
    );
    dispatch(
      postSectionAsync({
        data: {
          text: '',
          farey_order: getFareyOrderForLast(sortedSections),
          page: page,
        },
      }),
    );
  }

  const handleToggleEditing = useCallback(() => {
    setEditing((currentEditing) => {
      const nextEditing = !currentEditing;
      if (!nextEditing) {
        setSorting(false);
      }
      return nextEditing;
    });
  }, []);

  const listInfo: SortableListInfo = {
    id: page?.id as number,
    listType: SortableListType.Sections,
    items: sortByOrder((page?.sections ?? []) as AnyOrderObject[]),
  };

  return (
    <div
      className={classNames('static_page', className, {
        editing: editable && editing,
        sorting: editable && sorting,
      })}
    >
      <PageContextProvider
        editing={editing}
        sorting={sorting}
        setEditing={setEditing}
        setSorting={setSorting}
      >
        <div className="page_container">
          <div className="page_content" ref={scrollableRef}>
            {notfound ? <Error404 /> : null}
            {page ? (
              <SortableContainer reorder={reorderSections}>
                <SortableList
                  className="page_sections"
                  listInfo={listInfo}
                  accepting={[SortableItemType.Section]}
                  disabled={!editable}
                  containerRef={scrollableRef}
                >
                  <LiveContentWarning editing={editing} />
                  {editable && editing ? (
                    <div className="settings_label">
                      {getLanguageName(locale, locale)}
                    </div>
                  ) : null}
                  <section className="page_header">
                    {editable && editing ? (
                      <>
                        <h1
                          className="page_title"
                          contentEditable
                          suppressContentEditableWarning
                          // onInput={() => setEditing(true)}
                          onBlur={updateTitle}
                        >
                          {page.title}
                        </h1>
                      </>
                    ) : (
                      <h1>{page.title}</h1>
                    )}
                    {editable ? (
                      <div className="page_actions">
                        <Icon
                          symbol={
                            editing ? IconSymbol.visible : IconSymbol.pencil
                          }
                          onClick={handleToggleEditing}
                          size={20}
                          hintProps={{
                            hint: editing
                              ? { id: 'PAGES.PREVIEW_MODE' }
                              : { id: 'PAGES.EDIT_MODE' },
                          }}
                        />
                        <Icon
                          symbol={IconSymbol.sort}
                          variant={
                            sorting ? IconVariant.accent : IconVariant.dark
                          }
                          onClick={() => setSorting(!sorting)}
                          size={20}
                          hintProps={{
                            hint: sorting
                              ? { id: 'PAGES.PREVIEW_MODE' }
                              : { id: 'PAGES.SORT_MODE' },
                          }}
                        />
                      </div>
                    ) : null}
                  </section>
                  {listInfo.items?.map((section) => (
                    <SortableItem
                      key={section.id}
                      listInfo={listInfo}
                      item={section as AnyOrderObject}
                      itemType={SortableItemType.Section}
                      addHandle={false}
                      disabled={!editable || !sorting}
                    >
                      <Section
                        page={page}
                        section={section as ISection}
                        pageRef={scrollableRef}
                      />
                    </SortableItem>
                  ))}
                  {user &&
                  editing &&
                  (!page.sections || page.sections.length === 0) ? (
                    <Icon
                      symbol={IconSymbol.plus}
                      onClick={handleAddSection}
                      size={20}
                      hintProps={{ hint: { id: 'PAGES.ADD_SECTION' } }}
                    />
                  ) : null}
                </SortableList>
              </SortableContainer>
            ) : null}
          </div>
        </div>
      </PageContextProvider>
    </div>
  );
}
